import React from "react";

export const CalendarSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
      {/* Header skeleton */}
      <div className="bg-gradient-to-r from-gray-300 to-gray-400 p-6">
        <div className="flex items-center justify-between">
          {/* Title skeleton */}
          <div className="h-6 bg-gray-200 rounded w-32" />

          {/* Button group skeleton */}
          <div className="flex gap-2">
            <div className="h-8 bg-gray-200 rounded w-16" />
            <div className="h-8 bg-gray-200 rounded w-20" />
            <div className="h-8 bg-gray-200 rounded w-16" />
          </div>

          {/* Navigation buttons skeleton */}
          <div className="flex gap-2">
            <div className="h-8 bg-gray-200 rounded w-8" />
            <div className="h-8 bg-gray-200 rounded w-8" />
          </div>
        </div>
      </div>

      {/* Calendar grid skeleton */}
      <div className="p-4">
        {/* Day headers skeleton */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="h-8 bg-gray-200 rounded" />
          ))}
        </div>

        {/* Calendar cells skeleton */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 35 }).map((_, index) => (
            <div
              key={index}
              className="aspect-square bg-gray-100 rounded relative"
            >
              {/* Day number skeleton */}
              <div className="absolute top-2 left-2 h-4 w-6 bg-gray-200 rounded" />

              {/* Random event blocks */}
              {Math.random() > 0.7 && (
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="h-3 bg-gray-300 rounded mb-1" />
                  {Math.random() > 0.5 && (
                    <div className="h-3 bg-gray-300 rounded w-3/4" />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const EventCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded w-20" />
        </div>
        <div className="h-3 bg-gray-200 rounded w-16" />
      </div>

      {/* Title */}
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />

      {/* Description */}
      <div className="space-y-2 mb-3">
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-2/3" />
      </div>

      {/* Details */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded w-32" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded w-24" />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4">
        <div className="h-6 bg-gray-200 rounded w-16" />
        <div className="h-4 w-4 bg-gray-200 rounded" />
      </div>
    </div>
  );
};

export const SidebarSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Search bar skeleton */}
      <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />

      {/* Filter buttons skeleton */}
      <div className="flex gap-2">
        <div className="h-8 bg-gray-200 rounded-lg w-20 animate-pulse" />
        <div className="h-8 bg-gray-200 rounded-lg w-24 animate-pulse" />
        <div className="h-8 bg-gray-200 rounded-lg w-16 animate-pulse" />
      </div>

      {/* Event cards skeleton */}
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <EventCardSkeleton key={index} />
        ))}
      </div>
    </div>
  );
};

export const TimeGridSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
      {/* Header skeleton */}
      <div className="bg-gradient-to-r from-gray-300 to-gray-400 p-6">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 rounded w-40" />
          <div className="flex gap-2">
            <div className="h-8 bg-gray-200 rounded w-16" />
            <div className="h-8 bg-gray-200 rounded w-16" />
          </div>
        </div>
      </div>

      {/* Time grid skeleton */}
      <div className="flex">
        {/* Time labels */}
        <div className="w-16 border-r border-gray-200">
          {Array.from({ length: 24 }).map((_, index) => (
            <div
              key={index}
              className="h-12 border-b border-gray-100 flex items-center justify-center"
            >
              <div className="h-3 bg-gray-200 rounded w-8" />
            </div>
          ))}
        </div>

        {/* Time columns */}
        <div className="flex-1 grid grid-cols-7">
          {Array.from({ length: 7 }).map((_, dayIndex) => (
            <div key={dayIndex} className="border-r border-gray-200">
              {/* Day header */}
              <div className="h-8 bg-gray-100 flex items-center justify-center border-b border-gray-200">
                <div className="h-4 bg-gray-200 rounded w-8" />
              </div>

              {/* Time slots */}
              <div className="relative">
                {Array.from({ length: 24 }).map((_, hourIndex) => (
                  <div
                    key={hourIndex}
                    className="h-12 border-b border-gray-100 relative"
                  >
                    {/* Random event blocks */}
                    {Math.random() > 0.8 && (
                      <div
                        className="absolute inset-x-1 bg-gray-300 rounded"
                        style={{
                          top: `${Math.random() * 30}%`,
                          height: `${20 + Math.random() * 60}%`,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ListViewSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
      {/* Header skeleton */}
      <div className="bg-gradient-to-r from-gray-300 to-gray-400 p-6">
        <div className="h-6 bg-gray-200 rounded w-32" />
      </div>

      {/* List items skeleton */}
      <div className="divide-y divide-gray-200">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="p-4 flex items-center gap-4">
            {/* Time */}
            <div className="w-20 text-center">
              <div className="h-4 bg-gray-200 rounded w-12 mx-auto mb-1" />
              <div className="h-3 bg-gray-200 rounded w-16 mx-auto" />
            </div>

            {/* Event dot */}
            <div className="w-3 h-3 bg-gray-300 rounded-full" />

            {/* Event details */}
            <div className="flex-1">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarSkeleton;
